import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ButtonGroup } from '@/components/ui/button-group'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Input } from '@/components/ui/input'
import {
  TrendingUp,
  RefreshCw,
  Target,
  Wallet,
  PiggyBank,
  Loader2,
  Plus,
  Minus,
} from 'lucide-react'
import { useOrganization } from '@/contexts/organization-context'
import { api } from '@/services/api'
import { toast } from 'sonner'

// Stone theme colors
const colors = {
  primary: '#78716c',      // stone-500
  primaryDark: '#57534e',  // stone-600
  secondary: '#a8a29e',    // stone-400
  accent: '#d6d3d1',       // stone-300
  success: '#57534e',      // stone-600 for positive
  warning: '#a8a29e',      // stone-400 for neutral
  muted: '#e7e5e4',        // stone-200
  chart1: '#78716c',       // stone-500 - actual
  chart2: '#a8a29e',       // stone-400 - predicted
}

// Metric names from the data report service
const METRIC_NAMES = {
  revenue: 'total_revenue',  // Total revenue metric name
  spend: 'total_spent',      // Total spend metric name
  profit: 'profit',          // Profit metric name
  grossRate: 'roas',         // ROAS metric (displayed as Gross Rate)
  cashflow: 'cashflow',      // Cashflow metric name (for chart)
}

// Dimension name for date grouping
const DATE_DIMENSION = 'date'

interface MetricsData {
  totalRevenue: number
  totalRevenueChange: number
  totalSpent: number
  totalSpentChange: number
  profit: number
  profitChange: number
  grossRate: number
  grossRateChange: number
}

// Helper to format date for API (YYYY-MM-DD)
const formatDateForAPI = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

// Get date range for KPIs (last 90 days vs previous 90 days)
const getDateRange = (): { start: Date; end: Date; prevStart: Date; prevEnd: Date } => {
  const end = new Date()
  const start = new Date()
  const prevEnd = new Date()
  const prevStart = new Date()

  // Current period: last 90 days
  start.setDate(end.getDate() - 90)

  // Previous period: 90 days before that
  prevEnd.setDate(start.getDate() - 1)
  prevStart.setDate(prevEnd.getDate() - 90)

  return { start, end, prevStart, prevEnd }
}

export function OptimizerPage() {
  const { currentOrganization } = useOrganization()
  const navigate = useNavigate()
  // Dedupe the page's data/retrieve + forecast calls. The load-time effects
  // re-fire as org/goal state resolves, so each request was hitting the network
  // 2x+; routing them through the query cache (stable key per org+period)
  // collapses the duplicates while preserving the existing state logic.
  const queryClient = useQueryClient()

  const [cashflowData, setCashflowData] = useState<any[]>([])
  const [cashflowLoading, setCashflowLoading] = useState(true)
  const [metrics, setMetrics] = useState<MetricsData | null>(null)
  // All-time totals for the Performance Summary card (separate from the period KPI tiles)
  const [perfSummary, setPerfSummary] = useState<{ totalRevenue: number; totalSpent: number; profit: number; roas: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [goal, setGoal] = useState(0)
  const [goalType, setGoalType] = useState('')
  const [goalUnit, setGoalUnit] = useState<'percent' | 'dollar' | 'max' | null>(null)
  const [goalPeriod, setGoalPeriod] = useState<'yoy' | 'qoq' | 'mom' | 'by' | null>(null)
  const [goalTargetDate, setGoalTargetDate] = useState<Date | undefined>(undefined)
  const [isEditingGoal, setIsEditingGoal] = useState(false)
  const [goalInputValue, setGoalInputValue] = useState('')

  const getGoalLimits = () => {
    if (goalUnit === 'percent' || goalUnit === null) {
      return { min: 1, max: 1000, step: 1 }
    }
    return { min: 0, max: Infinity, step: 10000 }
  }

  // Format dollar values with K, M, B, T suffixes
  const formatDollarValue = (value: number): { display: string; suffix: string; divisor: number } => {
    if (value >= 1e12) {
      return { display: (value / 1e12).toFixed(value % 1e12 === 0 ? 0 : 1), suffix: 'T', divisor: 1e12 }
    } else if (value >= 1e9) {
      return { display: (value / 1e9).toFixed(value % 1e9 === 0 ? 0 : 1), suffix: 'B', divisor: 1e9 }
    } else if (value >= 1e6) {
      return { display: (value / 1e6).toFixed(value % 1e6 === 0 ? 0 : 1), suffix: 'M', divisor: 1e6 }
    } else {
      return { display: (value / 1e3).toFixed(value % 1e3 === 0 ? 0 : 1), suffix: 'K', divisor: 1e3 }
    }
  }

  const [goalDivisor, setGoalDivisor] = useState(1e3) // Track current divisor for editing

  const onGoalClick = (adjustment: number) => {
    const { min, max } = getGoalLimits()
    let step = adjustment
    if (goalUnit === 'dollar') {
      // Adjust step based on current magnitude
      const { divisor } = formatDollarValue(goal)
      step = adjustment > 0 ? divisor / 10 : -divisor / 10
    }
    setGoal(Math.max(min, Math.min(max, goal + step)))
  }

  const startEditingGoal = () => {
    if (goalUnit === 'percent') {
      setGoalInputValue(goal.toString())
    } else {
      const { display, divisor } = formatDollarValue(goal)
      setGoalInputValue(display)
      setGoalDivisor(divisor)
    }
    setIsEditingGoal(true)
  }

  const handleGoalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGoalInputValue(e.target.value)
  }

  const saveGoalEdit = () => {
    const { min, max } = getGoalLimits()
    let newValue = parseFloat(goalInputValue) || 0
    if (goalUnit === 'dollar') {
      newValue = newValue * goalDivisor
    }
    newValue = Math.max(min, Math.min(max, newValue))
    setGoal(newValue)
    setIsEditingGoal(false)
  }

  const handleGoalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveGoalEdit()
    } else if (e.key === 'Escape') {
      setIsEditingGoal(false)
    }
  }

  const [isSavingGoal, setIsSavingGoal] = useState(false)
  const [isGoalConfirmDialogOpen, setIsGoalConfirmDialogOpen] = useState(false)

  const handleSubmitGoalClick = () => {
    // If there's already a goal, show confirmation dialog
    if (currentOrganization?.goal) {
      setIsGoalConfirmDialogOpen(true)
    } else {
      handleSubmitGoal()
    }
  }

  const handleSubmitGoal = async () => {
    setIsGoalConfirmDialogOpen(false)
    if (!currentOrganization?.id) {
      toast.error('No organization selected')
      return
    }

    // Validate required fields
    if (!goalUnit) {
      toast.error('Please select a goal unit (%, $, or Max)')
      return
    }

    if (goalUnit !== 'max' && !goalPeriod) {
      toast.error('Please select a period (YoY, QoQ, MoM, or by)')
      return
    }

    // Validate date is required for "by" period
    if (goalPeriod === 'by' && !goalTargetDate) {
      toast.error('Please select a target date')
      return
    }

    setIsSavingGoal(true)
    try {
      // Build the goal object
      const operatorMap: Record<string, string> = {
        yoy: 'YoY',
        qoq: 'QoQ',
        mom: 'MoM',
        by: '>='
      }

      // For maximize, use 'maximize' operator and empty outcome
      const operator = goalUnit === 'max' ? 'maximize' : operatorMap[goalPeriod!]
      const outcome = goalUnit === 'max' ? '' : (goalUnit === 'percent' ? `${goal}%` : goal.toString())

      const goalData = {
        goal: {
          target: goalType,
          operator,
          outcome,
          by: goalUnit !== 'max' && goalPeriod === 'by' && goalTargetDate
            ? goalTargetDate.toISOString().split('T')[0]
            : ''
        }
      }

      await api.patch(`/organization/${currentOrganization.id}/goal`, goalData)
      toast.success('Goal saved successfully!')
    } catch (error: any) {
      console.error('Error saving goal:', error)
      toast.error(`Failed to save goal: ${error.response?.data?.detail || error.message}`)
    } finally {
      setIsSavingGoal(false)
    }
  }

  // Fetch KPI data from the data report service
  const fetchKPIData = async () => {
    if (!currentOrganization) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { start, end, prevStart, prevEnd } = getDateRange()

      // Fetch current period data
      const currentPayload = {
        organization_id: currentOrganization.id,
        output: 'json',
        metrics: [METRIC_NAMES.revenue, METRIC_NAMES.spend, METRIC_NAMES.profit, METRIC_NAMES.grossRate],
        dimensions: [],
        period: [formatDateForAPI(start), formatDateForAPI(end)],
        force_refresh: true,
      }
      const currentResponse = await queryClient.fetchQuery({
        queryKey: ['optimizer-kpi', currentOrganization.id, 'current', formatDateForAPI(start), formatDateForAPI(end)],
        queryFn: () => api.post('/data/retrieve', currentPayload),
        staleTime: 10_000,
        retry: false,
      })

      // Fetch previous period data for comparison
      const prevPayload = {
        organization_id: currentOrganization.id,
        output: 'json',
        metrics: [METRIC_NAMES.revenue, METRIC_NAMES.spend, METRIC_NAMES.profit, METRIC_NAMES.grossRate],
        dimensions: [],
        period: [formatDateForAPI(prevStart), formatDateForAPI(prevEnd)],
        force_refresh: true,
      }
      const prevResponse = await queryClient.fetchQuery({
        queryKey: ['optimizer-kpi', currentOrganization.id, 'prev', formatDateForAPI(prevStart), formatDateForAPI(prevEnd)],
        queryFn: () => api.post('/data/retrieve', prevPayload),
        staleTime: 10_000,
        retry: false,
      })

      // Extract totals from response (assuming single row when no dimensions)
      const currentData = currentResponse.data.data?.[0] || {}
      const prevData = prevResponse.data.data?.[0] || {}

      // Get metric values
      const currentRevenue = parseFloat(currentData[METRIC_NAMES.revenue]) || 0
      const currentSpend = parseFloat(currentData[METRIC_NAMES.spend]) || 0
      const currentProfit = parseFloat(currentData[METRIC_NAMES.profit]) || 0
      const currentGrossRate = parseFloat(currentData[METRIC_NAMES.grossRate]) || 0

      const prevRevenue = parseFloat(prevData[METRIC_NAMES.revenue]) || 0
      const prevSpend = parseFloat(prevData[METRIC_NAMES.spend]) || 0
      const prevProfit = parseFloat(prevData[METRIC_NAMES.profit]) || 0
      const prevGrossRate = parseFloat(prevData[METRIC_NAMES.grossRate]) || 0

      // Calculate percentage changes
      const calcChange = (current: number, previous: number): number => {
        if (previous === 0) return current > 0 ? 100 : 0
        return ((current - previous) / previous) * 100
      }

      setMetrics({
        totalRevenue: currentRevenue,
        totalRevenueChange: calcChange(currentRevenue, prevRevenue),
        totalSpent: currentSpend,
        totalSpentChange: calcChange(currentSpend, prevSpend),
        profit: currentProfit,
        profitChange: calcChange(currentProfit, prevProfit),
        grossRate: currentGrossRate,
        grossRateChange: calcChange(currentGrossRate, prevGrossRate),
      })
    } catch (error: any) {
      console.error('Error fetching KPI data:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load KPI data'
      toast.error('Failed to load metrics', { description: errorMessage })
      // Set empty metrics on error
      setMetrics({
        totalRevenue: 0,
        totalRevenueChange: 0,
        totalSpent: 0,
        totalSpentChange: 0,
        profit: 0,
        profitChange: 0,
        grossRate: 0,
        grossRateChange: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  // Generate mock goal data for the chart when no goal is set
  const generateMockGoalData = () => {
    setCashflowLoading(true)

    const data = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Calculate deadline based on goal period
    const calculateDeadline = (): Date => {
      const now = new Date()

      // For Maximize, use end of current month
      if (goalUnit === 'max') {
        return new Date(now.getFullYear(), now.getMonth() + 1, 0)
      }

      if (goalTargetDate) {
        // "by" period - use the specified target date
        return goalTargetDate
      }

      switch (goalPeriod) {
        case 'yoy':
          // End of current year
          return new Date(now.getFullYear(), 11, 31)
        case 'qoq':
          // End of current quarter
          const currentQuarter = Math.floor(now.getMonth() / 3)
          return new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0)
        case 'mom':
          // End of current month
          return new Date(now.getFullYear(), now.getMonth() + 1, 0)
        default:
          // Default to 90 days from now
          const defaultDeadline = new Date(now)
          defaultDeadline.setDate(defaultDeadline.getDate() + 90)
          return defaultDeadline
      }
    }

    const deadline = calculateDeadline()
    deadline.setHours(0, 0, 0, 0)
    // Use exact days to deadline, minimum 7 days for visibility
    const daysToDeadline = Math.max(7, Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))

    // Start 30 days in the past, end at goal deadline
    for (let i = -30; i <= daysToDeadline; i++) {
      const date = new Date(today)
      date.setDate(date.getDate() + i)

      const isPast = i <= 0
      const isDeadline = i === daysToDeadline && (goalPeriod !== null || goalUnit === 'max')
      const baseReal = 50000 + Math.random() * 20000
      // Predicted line grows towards the goal over time
      const progress = (i + 30) / (daysToDeadline + 30) // 0 to 1
      const basePredicted = 52000 + Math.random() * 10000 + (progress * 30000)

      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        real: isPast ? Math.round(baseReal) : null,
        predicted: Math.round(basePredicted),
        isToday: i === 0,
        isDeadline,
      })
    }

    setCashflowData(data)
    setCashflowLoading(false)
  }

  // Fetch all-time totals for the Performance Summary card.
  const fetchPerformanceSummary = async () => {
    if (!currentOrganization) return
    try {
      const payload = {
        organization_id: currentOrganization.id,
        output: 'json',
        metrics: [METRIC_NAMES.profit, METRIC_NAMES.revenue, METRIC_NAMES.spend, METRIC_NAMES.grossRate],
        dimensions: [],
        force_refresh: true,
        // period omitted -> all-time
      }
      const res = await queryClient.fetchQuery({
        queryKey: ['optimizer-perf-summary', currentOrganization.id],
        queryFn: () => api.post('/data/retrieve', payload),
        staleTime: 10_000,
        retry: false,
      })
      const row = res.data.data?.[0] || {}
      setPerfSummary({
        totalRevenue: parseFloat(row[METRIC_NAMES.revenue]) || 0,
        totalSpent: parseFloat(row[METRIC_NAMES.spend]) || 0,
        profit: parseFloat(row[METRIC_NAMES.profit]) || 0,
        roas: parseFloat(row[METRIC_NAMES.grossRate]) || 0,
      })
    } catch (error) {
      console.error('Error fetching performance summary:', error)
      setPerfSummary(null)
    }
  }

  // Fetch real goal forecast data from the optimizer API
  const fetchGoalForecast = async () => {
    // If no goal is set, use mock data
    if (!goalType || !currentOrganization) {
      generateMockGoalData()
      return
    }

    setCashflowLoading(true)

    try {
      const response = await queryClient.fetchQuery({
        queryKey: ['optimizer-forecast', currentOrganization.id, goalType, goalUnit, goalPeriod, goalTargetDate?.toISOString() ?? null],
        queryFn: () => api.get('/optimizer/forecast'),
        staleTime: 10_000,
        retry: false,
      })
      const forecast = response.data

      // Check if we got valid forecast data
      if (!forecast || !forecast.dates || forecast.dates.length === 0) {
        generateMockGoalData()
        return
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const todayStr = formatDateForAPI(today)

      // Transform API response to chart data format
      const chartData = forecast.dates.map((dateStr: string, index: number) => {
        const date = new Date(dateStr)
        const isToday = dateStr === todayStr
        const isDeadline = dateStr === forecast.goal_deadline

        return {
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          real: forecast.actual[index] !== null ? Math.round(forecast.actual[index]) : null,
          predicted: forecast.predicted[index] !== null ? Math.round(forecast.predicted[index]) : null,
          isToday,
          isDeadline,
        }
      })

      setCashflowData(chartData)
    } catch (error: any) {
      // Silently fall back to mock data - forecast errors are expected when no goal/agents configured
      const detail = error.response?.data?.detail
      if (detail && !detail.includes('Insufficient data') && !detail.includes('No agents')) {
        console.warn('Forecast error:', detail)
      }
      generateMockGoalData()
    } finally {
      setCashflowLoading(false)
    }
  }

  // Main function to load goal data - uses API when goal is set, mock data otherwise
  const loadGoalData = () => {
    if (goalType && currentOrganization) {
      fetchGoalForecast()
    } else {
      generateMockGoalData()
    }
  }

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchKPIData()
      fetchPerformanceSummary()
    }
  }, [currentOrganization?.id])

  // Reload goal chart data when goal settings change or org changes
  useEffect(() => {
    if (goalType && currentOrganization) {
      fetchGoalForecast()
    } else {
      generateMockGoalData()
    }
  }, [goalTargetDate, goalPeriod, goalUnit, goalType, currentOrganization?.id])

  // Load goal from organization when it changes
  useEffect(() => {
    if (currentOrganization?.goal) {
      const { target, operator, outcome, by } = currentOrganization.goal

      // Set goal type
      if (target) setGoalType(target)

      // Check for maximize operator first
      const lowerOperator = operator?.toLowerCase() || ''
      if (lowerOperator === 'maximize') {
        setGoalUnit('max')
      } else {
        // Set period based on operator (handle both YoY and YOY formats)
        if (['>=', '>', '='].includes(operator)) {
          // Absolute target with date
          setGoalPeriod('by')
        } else if (lowerOperator === 'yoy') {
          setGoalPeriod('yoy')
        } else if (lowerOperator === 'qoq') {
          setGoalPeriod('qoq')
        } else if (lowerOperator === 'mom') {
          setGoalPeriod('mom')
        }

        // Set goal value and unit
        if (outcome) {
          if (outcome.endsWith('%')) {
            setGoalUnit('percent')
            setGoal(parseFloat(outcome.replace('%', '')) || 15)
          } else {
            setGoalUnit('dollar')
            setGoal(parseFloat(outcome) || 100000)
          }
        }
      }

      // Set target date if present (parse as local date to avoid timezone issues)
      if (by) {
        const [year, month, day] = by.split('-').map(Number)
        if (year && month && day) {
          setGoalTargetDate(new Date(year, month - 1, day))
        }
      }
    } else {
      // Reset to empty/null values when no goal is set
      setGoal(0)
      setGoalType('')
      setGoalUnit(null)
      setGoalPeriod(null)
      setGoalTargetDate(undefined)
    }
  }, [currentOrganization])

  // Format goal description for chart title
  const getGoalDescription = (): string => {
    // Check if no goal is set
    if (!goalType || (goal === 0 && goalUnit !== 'max')) {
      return 'Not set yet'
    }

    const typeLabels: Record<string, string> = {
      revenue: 'Revenue Growth',
      profit: 'Net Profit',
      ebitda: 'EBITDA',
      cashflow: 'Free Cashflow',
    }
    const typeName = typeLabels[goalType] || goalType

    // For maximize, just return "Maximize {type}"
    if (goalUnit === 'max') {
      return `Maximize ${typeName}`
    }

    let valueStr = ''
    if (goalUnit === 'percent') {
      valueStr = `${goal}%`
    } else {
      const { display, suffix } = formatDollarValue(goal)
      valueStr = `$${display}${suffix}`
    }

    let periodStr = ''
    if (goalPeriod === 'yoy') {
      periodStr = ' YoY'
    } else if (goalPeriod === 'qoq') {
      periodStr = ' QoQ'
    } else if (goalPeriod === 'mom') {
      periodStr = ' MoM'
    } else if (goalPeriod === 'by' && goalTargetDate) {
      periodStr = ` by ${goalTargetDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
    }

    return `${typeName} ${valueStr}${periodStr}`
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatTooltipValue = (value: number) => {
    return formatCurrency(value)
  }

  // ---- Performance Summary (all-time) derived values ----
  const perfProfitMargin = perfSummary && perfSummary.totalRevenue > 0
    ? (perfSummary.profit / perfSummary.totalRevenue) * 100
    : null
  const perfRoas = perfSummary ? perfSummary.roas : null

  // Goal row: reflects the org's actual goal (any metric/mode). Fixed $ targets
  // show % attained; maximize/minimize goals show a status from the forecast trend.
  const GOAL_METRIC_LABELS: Record<string, string> = {
    revenue: 'Revenue', profit: 'Profit', ebitda: 'EBITDA', cashflow: 'Cashflow',
  }
  const NEUTRAL = 'bg-stone-200 text-stone-600'
  const GREEN = 'bg-green-100 text-green-700'
  const AMBER = 'bg-amber-100 text-amber-700'
  const goalOperator = (currentOrganization?.goal as { operator?: string } | undefined)?.operator || ''
  // Trend of the predicted (forecast) line: last minus first predicted value.
  const forecastPreds = cashflowData.map(d => d.predicted).filter((v): v is number => v != null)
  const forecastTrend = forecastPreds.length >= 2 ? forecastPreds[forecastPreds.length - 1] - forecastPreds[0] : null

  const goalRow = (() => {
    if (!goalType) {
      return { rowLabel: 'Goal', badge: { label: 'No goal set', cls: NEUTRAL } }
    }
    const metricLabel = GOAL_METRIC_LABELS[goalType] || (goalType.charAt(0).toUpperCase() + goalType.slice(1))

    // Fixed $ revenue target -> % attained (the only metric we have the value for here).
    if (goalUnit === 'dollar' && goalType === 'revenue' && goal > 0 && perfSummary) {
      const pct = (perfSummary.totalRevenue / goal) * 100
      const badge = pct >= 100 ? { label: 'Ahead', cls: GREEN }
        : pct >= 90 ? { label: 'On Track', cls: GREEN }
        : { label: 'Behind', cls: AMBER }
      return { rowLabel: `${metricLabel} (target)`, badge }
    }

    // Maximize / minimize (and other modes): status from the goal forecast trend.
    const modeLabel = goalOperator === 'minimize' ? 'minimize'
      : goalUnit === 'max' ? 'maximize'
      : goalUnit === 'percent' ? 'growth' : 'target'
    let badge: { label: string; cls: string }
    if (forecastTrend == null) {
      badge = { label: 'Goal set', cls: NEUTRAL }
    } else {
      const rising = forecastTrend > 0
      const onTrack = goalOperator === 'minimize' ? !rising : rising
      badge = onTrack ? { label: 'On Track', cls: GREEN } : { label: 'Behind', cls: AMBER }
    }
    return { rowLabel: `${metricLabel} (${modeLabel})`, badge }
  })()

  // Spend Efficiency: derived from ROAS (return on ad spend).
  const spendEfficiency = perfRoas == null
    ? { label: '—', cls: 'bg-stone-200 text-stone-600' }
    : perfRoas >= 1
      ? { label: 'Optimal', cls: 'bg-green-100 text-green-700' }
      : perfRoas >= 0.75
        ? { label: 'Fair', cls: 'bg-amber-100 text-amber-700' }
        : { label: 'Low', cls: 'bg-red-100 text-red-700' }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-stone-900">Optimizer</h2>
          <p className="text-stone-600">
            Track your business performance and optimize for growth
          </p>
        </div>
      </div>

      {/* Cashflow Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Drawer>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DrawerTrigger asChild>
                        <button
                          className="relative h-10 w-10 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors"
                        >
                          <Target className="h-5 w-5" />
                          {!currentOrganization?.goal && (
                            <span className="absolute inset-0 rounded-lg bg-stone-400 opacity-75 animate-ping" />
                          )}
                        </button>
                      </DrawerTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Set business goal</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <DrawerContent>
                  <div className="mx-auto w-full max-w-sm">
                    <DrawerHeader>
                      <DrawerTitle>Goal</DrawerTitle>
                      <DrawerDescription>Set your company's global goal.</DrawerDescription>
                    </DrawerHeader>
                    <div className="p-4 pb-0 space-y-6 flex flex-col items-center">
                      {/* Goal Type */}
                      <ButtonGroup>
                        <Button
                          variant={goalType === 'revenue' ? 'default' : 'outline'}
                          onClick={() => setGoalType('revenue')}
                        >
                          Revenue Growth
                        </Button>
                        <Button
                          variant={goalType === 'profit' ? 'default' : 'outline'}
                          onClick={() => setGoalType('profit')}
                        >
                          Net Profit
                        </Button>
                        <Button
                          variant={goalType === 'ebitda' ? 'default' : 'outline'}
                          onClick={() => setGoalType('ebitda')}
                        >
                          EBITDA
                        </Button>
                        <Button
                          variant={goalType === 'cashflow' ? 'default' : 'outline'}
                          onClick={() => setGoalType('cashflow')}
                        >
                          Free Cashflow
                        </Button>
                      </ButtonGroup>

                      {/* Unit and Period */}
                      <div className="flex items-center gap-4">
                        <ButtonGroup>
                          <Button
                            variant={goalUnit === 'percent' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setGoalUnit('percent')
                              setGoal(15) // Reset to sensible default
                            }}
                          >
                            %
                          </Button>
                          <Button
                            variant={goalUnit === 'dollar' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setGoalUnit('dollar')
                              setGoal(100000) // Reset to sensible default
                            }}
                          >
                            $
                          </Button>
                          <Button
                            variant={goalUnit === 'max' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setGoalUnit('max')
                            }}
                          >
                            Max
                          </Button>
                        </ButtonGroup>

                        {goalUnit !== 'max' && goalUnit !== null && <ButtonGroup>
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={goalPeriod === 'yoy' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setGoalPeriod('yoy')}
                                >
                                  YoY
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Year over Year</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={goalPeriod === 'qoq' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setGoalPeriod('qoq')}
                                >
                                  QoQ
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Quarter over Quarter</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={goalPeriod === 'mom' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setGoalPeriod('mom')}
                                >
                                  MoM
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Month over Month</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={goalPeriod === 'by' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setGoalPeriod('by')}
                                >
                                  by
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Target by specific date</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </ButtonGroup>}
                      </div>

                      {/* Date picker for "by" option */}
                      {goalUnit && goalUnit !== 'max' && goalPeriod === 'by' && (
                        <DatePickerInput
                          value={goalTargetDate}
                          onChange={setGoalTargetDate}
                          placeholder="Select target date"
                          minDate={new Date()}
                          className="w-48"
                        />
                      )}

                      {/* Goal Value */}
                      <div className="flex items-center justify-center space-x-2">
                        {goalUnit && goalUnit !== 'max' && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0 rounded-full"
                            onClick={() => onGoalClick(-1)}
                            disabled={goal <= getGoalLimits().min}
                          >
                            <Minus className="h-4 w-4" />
                            <span className="sr-only">Decrease</span>
                          </Button>
                        )}
                        <div className="flex-1 text-center min-w-[180px]">
                          {goalUnit === null ? (
                            <div className="text-2xl font-medium text-stone-400">
                              Select options above
                            </div>
                          ) : goalUnit === 'max' ? (
                            <div className="text-7xl font-bold tracking-tighter text-stone-800">
                              Maximize
                            </div>
                          ) : isEditingGoal ? (
                            <div className="relative inline-block">
                              {goalUnit === 'dollar' && (
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xl font-semibold text-stone-400">$</span>
                              )}
                              <Input
                                type="number"
                                value={goalInputValue}
                                onChange={handleGoalInputChange}
                                onKeyDown={handleGoalKeyDown}
                                onBlur={saveGoalEdit}
                                autoFocus
                                className="w-32 text-center text-3xl font-bold h-12 px-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xl font-semibold text-stone-400">
                                {goalUnit === 'percent' ? '%' : formatDollarValue(goal).suffix}
                              </span>
                            </div>
                          ) : (
                            <div
                              className="text-7xl font-bold tracking-tighter cursor-pointer hover:text-stone-600 transition-colors"
                              onClick={startEditingGoal}
                              title="Click to edit"
                            >
                              {goalUnit === 'percent' ? (
                                <>{goal}%</>
                              ) : (
                                <>${formatDollarValue(goal).display}{formatDollarValue(goal).suffix}</>
                              )}
                            </div>
                          )}
                          {goalUnit && goalUnit !== 'max' && goalPeriod && (
                            <div className="text-[0.70rem] uppercase text-muted-foreground">
                              {goalPeriod === 'yoy' && 'Year over Year'}
                              {goalPeriod === 'qoq' && 'Quarter over Quarter'}
                              {goalPeriod === 'mom' && 'Month over Month'}
                              {goalPeriod === 'by' && (goalTargetDate
                                ? `by ${goalTargetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                : 'Select target date'
                              )}
                            </div>
                          )}
                        </div>
                        {goalUnit && goalUnit !== 'max' && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0 rounded-full"
                            onClick={() => onGoalClick(1)}
                            disabled={goal >= getGoalLimits().max}
                          >
                            <Plus className="h-4 w-4" />
                            <span className="sr-only">Increase</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    <DrawerFooter>
                      <Button onClick={handleSubmitGoalClick} disabled={isSavingGoal}>
                        {isSavingGoal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        {isSavingGoal ? 'Saving...' : 'Submit'}
                      </Button>
                      <DrawerClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DrawerClose>
                    </DrawerFooter>
                  </div>
                </DrawerContent>
              </Drawer>
              <div>
                <CardTitle className="text-stone-800">Goal</CardTitle>
                <CardDescription>{getGoalDescription()}</CardDescription>
              </div>
            </div>
            {cashflowLoading ? (
              <RefreshCw className="h-4 w-4 text-stone-400 animate-spin" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-stone-400" />
                  <span className="text-sm text-stone-500">Predicted</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-stone-600" />
                  <span className="text-sm text-stone-500">Actual</span>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[260px]">
            {cashflowLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 text-stone-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-stone-500">Loading cashflow data...</p>
                </div>
              </div>
            ) : cashflowData.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-stone-500">No goal data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={cashflowData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.chart1} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={colors.chart1} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorPredicted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.chart2} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.chart2} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#78716c' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatCurrency}
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
                            <p className="text-sm font-medium text-stone-800 mb-2">{label}</p>
                            {payload.map((entry: any, index: number) => (
                              entry.value !== null && (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <div
                                    className="h-2 w-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-stone-500">{entry.name}:</span>
                                  <span className="font-medium text-stone-700">
                                    {formatTooltipValue(entry.value)}
                                  </span>
                                </div>
                              )
                            ))}
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="predicted"
                    stroke={colors.chart2}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    fill="url(#colorPredicted)"
                    name="Predicted"
                  />
                  <Area
                    type="monotone"
                    dataKey="real"
                    stroke={colors.chart1}
                    strokeWidth={2}
                    fill="url(#colorReal)"
                    name="Actual"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Section */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-stone-800">Performance Summary</CardTitle>
            <CardDescription className="text-stone-500">Key insights from your optimization</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">{goalRow.rowLabel}</span>
              <Badge variant="secondary" className={goalRow.badge.cls}>{goalRow.badge.label}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">Spend Efficiency</span>
              <Badge variant="secondary" className={spendEfficiency.cls}>{spendEfficiency.label}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">Profit Margin</span>
              <span className="text-sm font-medium text-stone-700">
                {perfProfitMargin != null ? `${perfProfitMargin.toFixed(1)}%` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">ROAS</span>
              <span className="text-sm font-medium text-stone-700">
                {perfRoas != null ? `${perfRoas.toFixed(2)}×` : '—'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base text-stone-800">Optimization Opportunities</CardTitle>
            <CardDescription className="text-stone-500">Recommendations to improve performance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-stone-200 p-1.5">
                <TrendingUp className="h-3 w-3 text-stone-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-700">Scale high-performing campaigns</p>
                <p className="text-xs text-stone-500">3 campaigns showing 30%+ gross rate</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-stone-200 p-1.5">
                <Wallet className="h-3 w-3 text-stone-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-700">Reduce spend on underperformers</p>
                <p className="text-xs text-stone-500">5 campaigns below target gross rate</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-stone-200 p-1.5">
                <PiggyBank className="h-3 w-3 text-stone-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-700">Potential profit increase</p>
                <p className="text-xs text-stone-500">Est. +$45K with optimizations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goal Change Confirmation Dialog */}
      <Dialog open={isGoalConfirmDialogOpen} onOpenChange={setIsGoalConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Goal</DialogTitle>
            <DialogDescription>
              You already have a goal set. Are you sure you want to replace it with a new goal?
              {goalUnit === 'percent' && goalPeriod && ['yoy', 'qoq', 'mom'].includes(goalPeriod) && (() => {
                const now = new Date()
                let periodLabel = ''
                let previousPeriod = ''

                if (goalPeriod === 'yoy') {
                  periodLabel = 'YoY'
                  previousPeriod = (now.getFullYear() - 1).toString()
                } else if (goalPeriod === 'qoq') {
                  periodLabel = 'QoQ'
                  const currentQuarter = Math.floor(now.getMonth() / 3) + 1
                  const prevQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1
                  const prevYear = currentQuarter === 1 ? now.getFullYear() - 1 : now.getFullYear()
                  previousPeriod = `${prevYear}Q${prevQuarter}`
                } else if (goalPeriod === 'mom') {
                  periodLabel = 'MoM'
                  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  previousPeriod = prevMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                }

                return (
                  <p className="mt-2 text-amber-600">
                    You've selected a <span className="font-bold">{periodLabel} %</span> goal. Make sure there is data for <span className="font-bold">{previousPeriod}</span>. Otherwise choose a fixed $ amount.
                  </p>
                )
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGoalConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitGoal} disabled={isSavingGoal}>
              {isSavingGoal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
